import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navigation from '../../components/feature/Navigation';
import Footer from '../../components/feature/Footer';
import StickyBooking from '../../components/feature/StickyBooking';
import { pathFor, type Lang } from '../../router/paths';

interface ZimmerData {
  name: string;
  images: string[];
}

const FALLBACK_BAD = '/images/zimmer/Komfort_DZ_Starnberger_See/Komfort_DZ_Starnberger_See_Bad_2025.jpg';

const doppelzimmer: ZimmerData[] = [
  { name: 'Ostersee',   images: ['/images/zimmer/DZ_Ostersee/DZ_Ostersee_Schlafbereich_mit_Sekt_2025.jpg', '/images/zimmer/DZ_Ostersee/DZ_Ostersee_Wohnbereich_2025.jpg', '/images/zimmer/DZ_Ostersee/Hotel_DZ_Ostersee_Bad.jpg'] },
  { name: 'Wörthsee',   images: ['/images/zimmer/DZ_Woerthsee/DZ_Woerthsee_Schlafbereich_2025.jpg', '/images/zimmer/DZ_Woerthsee/DZ_Woerthsee_Wohnbereich_2025.jpg', '/images/zimmer/DZ_Woerthsee/Hotel_DZ_Woerthsee_Bad_2.jpg'] },
  { name: 'Ammersee',    images: ['/images/zimmer/DZ_Ammersee/DZ_Ammersee_2025.jpg', '/images/zimmer/DZ_Ammersee/Hotel_DZ_Ammersee.jpg'] },
  { name: 'Kochelsee',  images: ['/images/zimmer/DZ_Kochelsee/DZ_Kochelsee_1_2025.jpg', '/images/zimmer/DZ_Kochelsee/DZ_Kochelsee_2_2025.jpg', '/images/zimmer/DZ_Kochelsee/Hotel_DZ_Kochelsee_Bad.jpg'] },
  { name: 'Tegernsee',  images: ['/images/zimmer/DZ_Tegernsee/DZ_Tegernsee_2025.jpg', FALLBACK_BAD] },
  { name: 'Staffelsee', images: ['/images/zimmer/DZ_Staffelsee/DZ_Staffelsee_2025.jpg', '/images/zimmer/DZ_Staffelsee/Hotel_DZ_Staffelsee_Bad.jpg'] },
  { name: 'Würmtal',    images: ['/images/zimmer/DZ_Wuermtal/DZ_Wuermtal_1_2025.jpg', '/images/zimmer/DZ_Wuermtal/DZ_Wuermtal_2_2025.jpg', '/images/zimmer/DZ_Wuermtal/DZ_Wuermtal_Bad_2025.jpg'] },
  { name: 'Eibsee',     images: ['/images/zimmer/DZ_Eibsee/DZ_Eibsee_2025.jpg', '/images/zimmer/DZ_Eibsee/DZ_Eibsee_Bad_2025.jpg'] },
  { name: 'Walchensee', images: ['/images/zimmer/DZ_Walchensee/DZ_Walchensee_2_2025.jpg', '/images/zimmer/DZ_Walchensee/DZ_Walchensee__1_2025.jpg', '/images/zimmer/DZ_Walchensee/DZ_Walchensee_Bad_2025.jpg'] },
];

const einzelzimmer: ZimmerData[] = [
  { name: 'Pilsensee',     images: ['/images/zimmer/EZ_Pilsensee/EZ_Pilsensee_2025.jpg', '/images/zimmer/EZ_Pilsensee/Hotel_EZ_Pilsensee_Bad.jpg'] },
  { name: 'Schliersee',    images: ['/images/zimmer/EZ_Schliersee/EZ_Schliersee_2025.jpg', '/images/zimmer/EZ_Schliersee/Hotel_EZ_Schliersee_Bad.jpg'] },
  { name: 'Wesslinger See',images: ['/images/zimmer/EZ_Wesslinger_See/EZ_Wesslinger_See_2025.jpg', '/images/zimmer/EZ_Wesslinger_See/Hotel_EZ_Wesslinger_See_Bad.jpg'] },
];

const komfortSliderImages = [
  '/images/zimmer/Komfort_DZ_Starnberger_See/Komfort_DZ_Starnberger_See_Wohnbereich_2025.jpg',
  '/images/zimmer/Komfort_DZ_Starnberger_See/Komfort_DZ_Starnberger_See_Schlafbereich_2025.jpg',
  '/images/zimmer/Komfort_DZ_Starnberger_See/Komfort_DZ_Starnberger_See_Bad_2025.jpg',
];

const amenitiesDZKeys = ['amenitySitzbereich', 'amenitySafe', 'amenityBad', 'amenitySchreibtisch', 'amenityTv', 'amenityPflege', 'amenityTeppich', 'amenitySat', 'amenityHaartrockner', 'amenitySchrank', 'amenityTel', 'amenityHausschuhe'];
const amenitiesEZKeys = amenitiesDZKeys;
const amenitiesKomfortKeys = ['amenityWohnSchlaf', 'amenitySafe', 'amenityBad', 'amenitySchreibtisch', 'amenityTv', 'amenityPflege', 'amenityTeppich', 'amenitySat', 'amenityHaartrockner', 'amenitySchrank', 'amenityTel', 'amenityHausschuhe'];

// Einzelne Zimmerkachel mit Mini-Slider
function ZimmerKachel({ zimmer, prefix, onOpenLightbox }: { zimmer: ZimmerData; prefix: string; onOpenLightbox: (images: string[], name: string) => void }) {
  const { t } = useTranslation();
  const [imgIdx, setImgIdx] = useState(0);
  return (
    <div
      className="bg-white rounded-xl overflow-hidden border border-stone-100 shadow-sm hover:shadow-md transition-all duration-300 cursor-pointer group"
      onClick={() => onOpenLightbox(zimmer.images, `${prefix} „${zimmer.name}"`)}
    >
      <div className="relative h-44 sm:h-52 overflow-hidden">
        {zimmer.images.map((src, i) => (
          <img
            key={src}
            src={src}
            alt={`${prefix} „${zimmer.name}" – Hotel Alpenhof Gauting`}
            loading="lazy"
            className="absolute inset-0 w-full h-full object-cover transition-opacity duration-500 group-hover:scale-105 transition-transform"
            style={{ opacity: imgIdx === i ? 1 : 0 }}
          />
        ))}
        {zimmer.images.length > 1 && (
          <>
            <button
              onClick={(e) => { e.stopPropagation(); setImgIdx(i => (i - 1 + zimmer.images.length) % zimmer.images.length); }}
              aria-label={t('sliderPrev')}
              className="absolute left-1 top-1/2 -translate-y-1/2 text-white/85 hover:text-white transition-colors cursor-pointer z-10"
              style={{ filter: 'drop-shadow(0 1px 3px rgba(0,0,0,0.7))' }}
            >
              <i className="ri-arrow-left-s-line text-2xl leading-none" />
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); setImgIdx(i => (i + 1) % zimmer.images.length); }}
              aria-label={t('sliderNext')}
              className="absolute right-1 top-1/2 -translate-y-1/2 text-white/85 hover:text-white transition-colors cursor-pointer z-10"
              style={{ filter: 'drop-shadow(0 1px 3px rgba(0,0,0,0.7))' }}
            >
              <i className="ri-arrow-right-s-line text-2xl leading-none" />
            </button>
          </>
        )}
      </div>
      <div className="px-3 py-2.5">
        <p className="text-sm font-semibold text-stone-800">{prefix} „{zimmer.name}"</p>
      </div>
    </div>
  );
}

export default function RoomsPage() {
  const { t, i18n } = useTranslation();
  const lang: Lang = i18n.language === 'en' ? 'en' : 'de';
  const buchenPath = pathFor('buchen', lang);
  const [expandedDZ, setExpandedDZ] = useState(false);
  const [expandedEZ, setExpandedEZ] = useState(false);
  const [expandedAmenityDZ, setExpandedAmenityDZ] = useState(false);
  const [expandedAmenityEZ, setExpandedAmenityEZ] = useState(false);
  const [expandedAmenityKomfort, setExpandedAmenityKomfort] = useState(false);
  const [komfortImg, setKomfortImg] = useState(0);
  const [activeCategory, setActiveCategory] = useState('alle');
  const [lightbox, setLightbox] = useState<{ images: string[]; name: string; idx: number } | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const timer = setInterval(() => setKomfortImg(i => (i + 1) % komfortSliderImages.length), 4000);
    return () => clearInterval(timer);
  }, []);

  const scrollToSection = (id: string) => {
    const el = document.getElementById(id);
    if (el) {
      window.scrollTo({ top: el.getBoundingClientRect().top + window.pageYOffset - 100, behavior: 'smooth' });
      setActiveCategory(id);
    }
  };

  const openLightbox = (images: string[], name: string) => setLightbox({ images, name, idx: 0 });
  const closeLightbox = () => setLightbox(null);

  return (
    <div className="min-h-screen bg-white">
      <Navigation />

      {/* Hero */}
      {/* MOBILE: h-[75vh] statt 110vh – kein übermäßiger Scroll-Overhead auf kleinen Screens */}
      <section className="relative h-[75vh] sm:h-[80vh] sm:min-h-[640px] flex items-end sm:items-center justify-center overflow-hidden pb-12 sm:pb-0">
        <div className="absolute inset-0">
          <img
            src="/images/zimmer/DZ_Wuermtal/DZ_Wuermtal_1_2025.jpg"
            alt="Doppelzimmer Würmtal – Hotel Alpenhof Gauting bei München & Starnberger See"
            loading="eager"
            fetchPriority="high"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-black/20" />
        </div>
        <div className="relative z-10 w-full px-4 sm:px-6 text-left sm:pt-44">
          <div className="max-w-5xl mx-auto text-white">
            <p className="text-[10px] sm:text-xs font-bold tracking-wider sm:tracking-widest uppercase mb-3 sm:mb-4 text-amber-300">{t('zimmerKicker')}</p>
            <h1 className="text-3xl sm:text-5xl md:text-7xl font-serif leading-snug sm:leading-tight mb-3 sm:mb-6">
              {t('zimmerHeroTitle1')}<br />
              <span className="text-amber-200">{t('zimmerHeroTitle2')}</span>
            </h1>
            <p className="text-sm sm:text-lg text-white/80 max-w-xl leading-relaxed">
              {t('zimmerHeroLead')}
            </p>
          </div>
        </div>
      </section>

      {/* Sticky Nav */}
      {/* MOBILE: top-14 statt hardcoded 72px – passt zur Nav-Höhe (h-14 mobil / h-20 desktop) */}
      {/* MOBILE: py-2.5 erhöht Touch-Target der Buttons auf ~44px */}
      <div className="sticky top-14 lg:top-20 z-40 bg-white/95 backdrop-blur-sm border-b border-stone-200 shadow-sm">
        <div className="max-w-5xl mx-auto px-4 py-3 sm:py-4">
          <div className="grid grid-cols-2 gap-2 sm:flex sm:gap-3 sm:overflow-x-auto sm:scrollbar-hide">
            {[
              { id: 'alle', label: t('zimmerFilterAll'), action: () => { setActiveCategory('alle'); window.scrollTo({ top: 0, behavior: 'smooth' }); } },
              { id: 'doppelzimmer', label: t('zimmerFilterDoppel'), action: () => scrollToSection('doppelzimmer') },
              { id: 'einzelzimmer', label: t('zimmerFilterEinzel'), action: () => scrollToSection('einzelzimmer') },
              { id: 'komfort-doppelzimmer', label: t('zimmerFilterKomfort'), action: () => scrollToSection('komfort-doppelzimmer') },
            ].map(({ id, label, action }) => (
              <button
                key={id}
                onClick={action}
                className={`px-3 py-2.5 sm:px-6 sm:py-2.5 rounded-full font-medium text-xs sm:text-sm sm:whitespace-nowrap transition-all cursor-pointer text-center ${
                  activeCategory === id ? 'bg-amber-700 text-white shadow-md' : 'bg-stone-100 text-stone-700 hover:bg-stone-200'
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Zimmer */}
      <section className="py-16 sm:py-20 px-4 sm:px-6">
        <div className="max-w-5xl mx-auto space-y-14">

          {/* Doppelzimmer */}
          <div id="doppelzimmer" className="bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 border border-stone-100">
            <div className="grid grid-cols-1 lg:grid-cols-2">
              {/* Bild links */}
              <div className="relative overflow-hidden self-start">
                <img
                  src="/images/zimmer/DZ_Wuermtal/DZ_Wuermtal_2_2025.jpg"
                  alt="Doppelzimmer Würmtal – Hotel Alpenhof Gauting nahe München"
                  loading="lazy"
                  className="w-full h-auto block"
                />
                <div className="absolute top-5 right-5 bg-white/95 backdrop-blur-sm px-4 py-2 rounded-full shadow-lg text-center">
                  <p className="text-amber-700 font-bold text-base">{t('zimmerDZPrice')}</p>
                  <p className="text-[11px] text-stone-500">{t('zimmerDZPriceNote')}</p>
                </div>
              </div>
              {/* Facts rechts */}
              <div className="p-6 sm:p-8 flex flex-col justify-center">
                <h3 className="text-2xl sm:text-3xl font-serif text-stone-900 mb-3">{t('zimmerDZTitle')}</h3>
                <div className="flex flex-wrap gap-4 text-sm text-stone-600 mb-4">
                  <span className="flex items-center gap-1.5"><i className="ri-ruler-line text-amber-600"></i>{t('zimmerDZSize')}</span>
                  <span className="flex items-center gap-1.5"><i className="ri-user-line text-amber-600"></i>{t('zimmerDZGuests')}</span>
                </div>
                <div className="flex flex-wrap gap-2 mb-5">
                  {[t('zimmerDZTag1'), t('zimmerDZTag2'), t('zimmerDZTag3')].map(f => (
                    <span key={f} className="px-3 py-1 rounded-full text-xs font-medium bg-amber-50 text-amber-600 border border-amber-200">{f}</span>
                  ))}
                </div>
                <p className="text-stone-700 leading-relaxed mb-6">
                  {t('zimmerDZDescription')}
                </p>
                <div className="flex items-center justify-between flex-wrap gap-4">
                  <button
                    onClick={() => setExpandedAmenityDZ(!expandedAmenityDZ)}
                    className="flex items-center gap-2 text-amber-700 font-medium hover:text-amber-600 transition-colors cursor-pointer text-sm"
                  >
                    <span>{expandedAmenityDZ ? t('zimmerToggleAmenitiesHide') : t('zimmerToggleAmenitiesShow')}</span>
                    <i className={`ri-arrow-${expandedAmenityDZ ? 'up' : 'down'}-s-line`}></i>
                  </button>
                  <button
                    onClick={() => navigate(buchenPath)}
                    className="inline-flex items-center gap-2 bg-amber-700 text-white px-6 py-2.5 rounded-md font-semibold text-sm hover:bg-amber-600 transition-all shadow-md cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-calendar-check-line"></i>
                    {t('zimmerBookCta')}
                  </button>
                </div>
              </div>
            </div>
            {/* Ausklappbare Bereiche – volle Breite */}
            <div className="px-6 sm:px-8 pb-6 sm:pb-8">
              {expandedAmenityDZ && (
                <div className="pt-5 border-t border-stone-200">
                  <h4 className="font-semibold text-stone-900 mb-3 text-sm">{t('zimmerAmenitiesTitle')}</h4>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                    {amenitiesDZKeys.map((k) => (
                      <div key={k} className="flex items-center gap-2 text-sm text-stone-700">
                        <i className="ri-check-line text-amber-600 flex-shrink-0"></i><span>{t(k)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <div className={`${expandedAmenityDZ ? 'mt-6' : ''} pt-5 border-t border-stone-100`}>
                <button
                  onClick={() => setExpandedDZ(!expandedDZ)}
                  className="flex items-center gap-2 text-stone-900 font-semibold border border-stone-300 rounded-full px-4 py-2 hover:border-amber-700 hover:text-amber-700 transition-all cursor-pointer text-sm bg-stone-50 hover:bg-amber-50"
                >
                  <i className="ri-hotel-bed-line text-base"></i>
                  <span>{expandedDZ ? t('zimmerToggleAllDZHide') : t('zimmerToggleAllDZShow')}</span>
                  <i className={`ri-arrow-${expandedDZ ? 'up' : 'down'}-s-line`}></i>
                </button>
                {expandedDZ && (
                  <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                    {doppelzimmer.map(z => (
                      <ZimmerKachel key={z.name} zimmer={z} prefix={t('zimmerPrefixDZ')} onOpenLightbox={openLightbox} />
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Einzelzimmer */}
          <div id="einzelzimmer" className="bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 border border-stone-100">
            <div className="grid grid-cols-1 lg:grid-cols-2">
              {/* Bild links */}
              <div className="relative overflow-hidden self-start">
                <img
                  src="/images/zimmer/EZ_Schliersee/EZ_Schliersee_2025.jpg"
                  alt="Einzelzimmer Schliersee – Hotel Alpenhof Gauting nahe Starnberger See"
                  loading="lazy"
                  className="w-full h-auto block"
                />
                <div className="absolute top-5 right-5 bg-white/95 backdrop-blur-sm px-4 py-2 rounded-full shadow-lg text-center">
                  <p className="text-amber-700 font-bold text-base">{t('zimmerEZPrice')}</p>
                  <p className="text-[11px] text-stone-500">{t('zimmerDZPriceNote')}</p>
                </div>
              </div>
              {/* Facts rechts */}
              <div className="p-6 sm:p-8 flex flex-col justify-center">
                <h3 className="text-2xl sm:text-3xl font-serif text-stone-900 mb-3">{t('zimmerEZTitle')}</h3>
                <div className="flex flex-wrap gap-4 text-sm text-stone-600 mb-4">
                  <span className="flex items-center gap-1.5"><i className="ri-ruler-line text-amber-600"></i>{t('zimmerEZSize')}</span>
                  <span className="flex items-center gap-1.5"><i className="ri-user-line text-amber-600"></i>{t('zimmerEZGuests')}</span>
                </div>
                <div className="flex flex-wrap gap-2 mb-5">
                  {[t('zimmerEZTag1'), t('zimmerEZTag2'), t('zimmerEZTag3'), t('zimmerEZTag4')].map(f => (
                    <span key={f} className="px-3 py-1 rounded-full text-xs font-medium bg-amber-50 text-amber-600 border border-amber-200">{f}</span>
                  ))}
                </div>
                <p className="text-stone-700 leading-relaxed mb-6">
                  {t('zimmerEZDescription')}
                </p>
                <div className="flex items-center justify-between flex-wrap gap-4">
                  <button
                    onClick={() => setExpandedAmenityEZ(!expandedAmenityEZ)}
                    className="flex items-center gap-2 text-amber-700 font-medium hover:text-amber-600 transition-colors cursor-pointer text-sm"
                  >
                    <span>{expandedAmenityEZ ? t('zimmerToggleAmenitiesHide') : t('zimmerToggleAmenitiesShow')}</span>
                    <i className={`ri-arrow-${expandedAmenityEZ ? 'up' : 'down'}-s-line`}></i>
                  </button>
                  <button
                    onClick={() => navigate(buchenPath)}
                    className="inline-flex items-center gap-2 bg-amber-700 text-white px-6 py-2.5 rounded-md font-semibold text-sm hover:bg-amber-600 transition-all shadow-md cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-calendar-check-line"></i>
                    {t('zimmerBookCta')}
                  </button>
                </div>
              </div>
            </div>
            {/* Ausklappbare Bereiche – volle Breite */}
            <div className="px-6 sm:px-8 pb-6 sm:pb-8">
              {expandedAmenityEZ && (
                <div className="pt-5 border-t border-stone-200">
                  <h4 className="font-semibold text-stone-900 mb-3 text-sm">{t('zimmerAmenitiesTitle')}</h4>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                    {amenitiesEZKeys.map((k) => (
                      <div key={k} className="flex items-center gap-2 text-sm text-stone-700">
                        <i className="ri-check-line text-amber-600 flex-shrink-0"></i><span>{t(k)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <div className={`${expandedAmenityEZ ? 'mt-6' : ''} pt-5 border-t border-stone-100`}>
                <button
                  onClick={() => setExpandedEZ(!expandedEZ)}
                  className="flex items-center gap-2 text-stone-900 font-semibold border border-stone-300 rounded-full px-4 py-2 hover:border-amber-700 hover:text-amber-700 transition-all cursor-pointer text-sm bg-stone-50 hover:bg-amber-50"
                >
                  <i className="ri-hotel-bed-line text-base"></i>
                  <span>{expandedEZ ? t('zimmerToggleAllEZHide') : t('zimmerToggleAllEZShow')}</span>
                  <i className={`ri-arrow-${expandedEZ ? 'up' : 'down'}-s-line`}></i>
                </button>
                {expandedEZ && (
                  <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                    {einzelzimmer.map(z => (
                      <ZimmerKachel key={z.name} zimmer={z} prefix={t('zimmerPrefixEZ')} onOpenLightbox={openLightbox} />
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Komfort-Doppelzimmer */}
          <div id="komfort-doppelzimmer" className="bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 border border-stone-100">
            <div className="grid grid-cols-1 lg:grid-cols-2">
              {/* Bild links mit Slider */}
              <div className="relative overflow-hidden self-start">
                {komfortSliderImages.map((src, i) => (
                  <img
                    key={src}
                    src={src}
                    alt="Komfort-Doppelzimmer Starnberger See – Hotel Alpenhof Gauting"
                    loading="lazy"
                    className={`w-full h-auto transition-opacity duration-700 ${i === 0 ? 'block' : 'absolute inset-0'}`}
                    style={{ opacity: komfortImg === i ? 1 : 0 }}
                  />
                ))}
                <div className="absolute top-5 right-5 bg-white/95 backdrop-blur-sm px-4 py-2 rounded-full shadow-lg text-center">
                  <p className="text-amber-700 font-bold text-base">{t('zimmerKomfortPrice')}</p>
                  <p className="text-[11px] text-stone-500">{t('zimmerDZPriceNote')}</p>
                </div>
                <button
                  onClick={() => setKomfortImg(i => (i - 1 + komfortSliderImages.length) % komfortSliderImages.length)}
                  aria-label={t('sliderPrev')}
                  className="absolute left-2 top-1/2 -translate-y-1/2 text-white/85 hover:text-white transition-colors cursor-pointer z-10"
                  style={{ filter: 'drop-shadow(0 1px 3px rgba(0,0,0,0.7))' }}
                >
                  <i className="ri-arrow-left-s-line text-3xl leading-none" />
                </button>
                <button
                  onClick={() => setKomfortImg(i => (i + 1) % komfortSliderImages.length)}
                  aria-label={t('sliderNext')}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-white/85 hover:text-white transition-colors cursor-pointer z-10"
                  style={{ filter: 'drop-shadow(0 1px 3px rgba(0,0,0,0.7))' }}
                >
                  <i className="ri-arrow-right-s-line text-3xl leading-none" />
                </button>
                <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-1.5">
                  {komfortSliderImages.map((_, i) => (
                    <button key={i} onClick={() => setKomfortImg(i)}
                      className={`w-2 h-2 rounded-full transition-all cursor-pointer ${i === komfortImg ? 'bg-white scale-125' : 'bg-white/50'}`}
                    />
                  ))}
                </div>
              </div>
              {/* Facts rechts */}
              <div className="p-6 sm:p-8 flex flex-col justify-center">
                <h3 className="text-2xl sm:text-3xl font-serif text-stone-900 mb-3">{t('zimmerKomfortTitle')}</h3>
                <div className="flex flex-wrap gap-4 text-sm text-stone-600 mb-4">
                  <span className="flex items-center gap-1.5"><i className="ri-ruler-line text-amber-600"></i>{t('zimmerKomfortSize')}</span>
                  <span className="flex items-center gap-1.5"><i className="ri-user-line text-amber-600"></i>{t('zimmerKomfortGuests')}</span>
                </div>
                <div className="flex flex-wrap gap-2 mb-5">
                  {[t('zimmerKomfortTag1'), t('zimmerKomfortTag2'), t('zimmerKomfortTag3'), t('zimmerKomfortTag4'), t('zimmerKomfortTag5')].map(f => (
                    <span key={f} className="px-3 py-1 rounded-full text-xs font-medium bg-amber-50 text-amber-600 border border-amber-200">{f}</span>
                  ))}
                </div>
                <p className="text-stone-700 leading-relaxed mb-6">
                  {t('zimmerKomfortDescription')}
                </p>
                <div className="flex items-center justify-between flex-wrap gap-4">
                  <button
                    onClick={() => setExpandedAmenityKomfort(!expandedAmenityKomfort)}
                    className="flex items-center gap-2 text-amber-700 font-medium hover:text-amber-600 transition-colors cursor-pointer text-sm"
                  >
                    <span>{expandedAmenityKomfort ? t('zimmerToggleAmenitiesHide') : t('zimmerToggleAmenitiesShow')}</span>
                    <i className={`ri-arrow-${expandedAmenityKomfort ? 'up' : 'down'}-s-line`}></i>
                  </button>
                  <button
                    onClick={() => navigate(buchenPath)}
                    className="inline-flex items-center gap-2 bg-amber-700 text-white px-6 py-2.5 rounded-md font-semibold text-sm hover:bg-amber-600 transition-all shadow-md cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-calendar-check-line"></i>
                    {t('zimmerBookCta')}
                  </button>
                </div>
              </div>
            </div>
            {/* Ausklappbare Bereiche – volle Breite */}
            <div className="px-6 sm:px-8 pb-6 sm:pb-8">
              {expandedAmenityKomfort && (
                <div className="pt-5 border-t border-stone-200">
                  <h4 className="font-semibold text-stone-900 mb-3 text-sm">{t('zimmerAmenitiesTitle')}</h4>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                    {amenitiesKomfortKeys.map((k) => (
                      <div key={k} className="flex items-center gap-2 text-sm text-stone-700">
                        <i className="ri-check-line text-amber-600 flex-shrink-0"></i><span>{t(k)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

        </div>
      </section>

      {/* Wichtige Informationen */}
      <section className="py-14 px-4 sm:px-6 bg-stone-50">
        <div className="max-w-5xl mx-auto">
          <div className="rounded-xl bg-amber-50 border border-amber-200 p-6 sm:p-8">
            <div className="flex items-start gap-4 mb-6">
              <div className="w-10 h-10 rounded-full bg-amber-700 flex items-center justify-center flex-shrink-0">
                <i className="ri-information-line text-white text-lg"></i>
              </div>
              <h3 className="text-xl sm:text-2xl font-serif text-stone-900 pt-1">{t('zimmerInfoTitle')}</h3>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm text-stone-700">
              <p><strong>{t('zimmerInfoCheckin')}</strong> {t('zimmerInfoCheckinValue')}</p>
              <p><strong>{t('zimmerInfoCheckout')}</strong> {t('zimmerInfoCheckoutValue')}</p>
              <p className="sm:col-span-2"><strong>{t('zimmerInfoBreakfast')}</strong> {t('zimmerInfoBreakfastValue')}</p>
              <p><strong>{t('zimmerInfoParking')}</strong> {t('zimmerInfoParkingValue')}</p>
              <p><strong>{t('zimmerInfoWifi')}</strong> {t('zimmerInfoWifiValue')}</p>
              <p className="sm:col-span-2"><strong>{t('zimmerInfoPets')}</strong> {t('zimmerInfoPetsValue')}</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 sm:py-24 px-4 sm:px-6 bg-amber-700">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-serif text-white mb-4 leading-tight">
            {t('zimmerCtaTitle')}
          </h2>
          <p className="text-base sm:text-lg text-amber-100 leading-relaxed mb-8 max-w-2xl mx-auto">
            {t('zimmerCtaText')}
          </p>
          <button
            onClick={() => navigate(buchenPath)}
            className="inline-flex items-center gap-2 bg-white text-amber-700 px-8 py-4 rounded-md font-semibold hover:bg-amber-50 transition-colors shadow-lg cursor-pointer whitespace-nowrap"
          >
            {t('zimmerCtaButton')}
            <i className="ri-arrow-right-line"></i>
          </button>
        </div>
      </section>

      <Footer />
      <StickyBooking />

      {/* Lightbox */}
      {lightbox && (
        <div className="fixed inset-0 z-50 bg-black/85 flex items-center justify-center p-4" onClick={closeLightbox}>
          <button className="absolute top-5 right-6 text-white text-4xl cursor-pointer hover:text-amber-300 transition-colors" onClick={closeLightbox}>
            <i className="ri-close-line"></i>
          </button>
          <div className="relative max-w-4xl w-full" onClick={e => e.stopPropagation()}>
            <p className="text-center text-white/70 text-sm mb-3">{lightbox.name}</p>
            <img
              src={lightbox.images[lightbox.idx]}
              alt={`${lightbox.name} – Hotel Alpenhof Gauting`}
              className="w-full max-h-[80vh] object-contain rounded-lg shadow-2xl"
            />
            {lightbox.images.length > 1 && (
              <div className="flex justify-center gap-2 mt-4">
                {lightbox.images.map((_, i) => (
                  <button key={i} onClick={() => setLightbox({ ...lightbox, idx: i })}
                    className={`w-2.5 h-2.5 rounded-full transition-all cursor-pointer ${i === lightbox.idx ? 'bg-white scale-125' : 'bg-white/40 hover:bg-white/70'}`}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
